import zod, { type ZodSchema } from 'zod';

export const PAN_VALIDATION_REGEX_STR = '[A-Z]{3}[ABCFGHLJPT][A-Z][0-9]{4}[A-Z]';
export const GST_VALIDATION_REGEX_STR = `\\d{2}${PAN_VALIDATION_REGEX_STR}[A-Z\\d]{1}[Z]{1}[A-Z\\d]{1}`;

export const GST = zod
  .union([
    zod.string().regex(new RegExp(`^${GST_VALIDATION_REGEX_STR}$`), { message: 'Please enter valid GST' }),
    zod.string().length(0),
  ])
  .optional();

export const PAN = zod
  .union([
    zod.string().regex(new RegExp(`^${PAN_VALIDATION_REGEX_STR}$`), { message: 'Please enter valid PAN' }),
    zod.string().length(0),
  ])
  .optional();

export type MarkOneRequired<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export const AccountCategory = zod.object({
  id: zod.string().optional(),
  name: zod.string().min(1, { message: 'Name should be atleast 1 characters long' }),
  parent_id: zod.union([zod.string().optional(), zod.string().uuid()]),
  parent_name: zod.string().optional(),
});

// eslint-disable-next-line no-redeclare
export type AccountCategory = zod.infer<typeof AccountCategory>;

export enum PaymentType {
  Cash = 'cash',
  Credit = 'credit',
}

export const IntToPaymentType = {
  0: PaymentType.Cash,
  1: PaymentType.Credit,
};

export enum TransactionType {
  Purchase = 'purchase',
  Sale = 'sale',
  Expense = 'expense',
  Income = 'income',
  Receipt = 'receipt',
  Payment = 'payment',
  Journal = 'journal',
  StockJournal = 'stock_journal',
}

export const IntToTransactionType = {
  0: TransactionType.Purchase,
  1: TransactionType.Sale,
  2: TransactionType.Payment,
  3: TransactionType.Journal,
  4: TransactionType.Expense,
  5: TransactionType.Income,
  6: TransactionType.Receipt,
  7: TransactionType.StockJournal,
};

export enum TransactionStatus {
  Active = 'active',
  Draft = 'draft',
}

export const IntToTransactionStatus = {
  0: TransactionStatus.Active,
  1: TransactionStatus.Draft,
};

export enum CRDR {
  CR = 'CR',
  DR = 'DR',
}

export const IntToCRDR = {
  0: CRDR.CR,
  1: CRDR.DR,
};

export const Amount = zod.coerce.number({ invalid_type_error: 'Invalid value' }).optional();
// eslint-disable-next-line no-redeclare
export type Amount = zod.infer<typeof Amount>;

export const NewAmount = zod.object({
  amount: Amount,
  crdr: zod.nativeEnum(CRDR),
});

// eslint-disable-next-line no-redeclare
export type NewAmount = zod.infer<typeof NewAmount>;

export const Email = zod.string().email();
// eslint-disable-next-line no-redeclare
export type Email = zod.infer<typeof Email>;

export const Tax = zod.object({
  randId: zod.string().optional(),
  id: zod.string().optional(),
  account_name: zod.string().optional(),
  rate: Amount,
  amount: Amount,
  account_id: zod.string().uuid(),
  master_name: zod.string().optional(),
  original_rate: Amount.optional(),
  transaction_types: zod.array(zod.nativeEnum(TransactionType)).optional(),
});

// eslint-disable-next-line no-redeclare
export type Tax = zod.infer<typeof Tax>;

export const Tag = zod.object({
  id: zod.union([zod.number(), zod.string().optional()]),
  name: zod.string(),
  label: zod.string().optional(),
});
// eslint-disable-next-line no-redeclare
export type Tag = zod.infer<typeof Tag>;

export interface Entry {
  id?: string;
  transaction_id: string;
  date: string;
  master_name: string;
  master_id: string;
  product_name: string;
  product_id: string;
  amount: number;
  quantity?: number;
  quantity_symbol?: string;
  rate?: number;
  type: TransactionType;
  closing_value?: number;
  age?: number;
  counter_master_id?: string;
  counter_master_name?: string;
  field_type?: string;
  reference_id?: string;
  description?: string;
  tags?: number[];
}

export interface StockEntry extends Entry {
  entry: Entry;
  sold_quantity?: number;
  counter_entries?: Entry[];
  gain?: number;
}

export const Bank = zod.object({
  randId: zod.string(),
  id: zod.string(),
  // amount: zod.union([Amount, NewAmount]),
  amount: Amount,
});
// eslint-disable-next-line no-redeclare
export type Bank = zod.infer<typeof Bank>;

export const Party = zod.object({
  id: zod.string(),
  name: zod.string(),
  gst: GST.nullable(),
});
// eslint-disable-next-line no-redeclare
export type Party = zod.infer<typeof Party>;

export const Product = zod.object({
  randId: zod.string(),
  id: zod.string(),
  quantity: Amount,
  rate: Amount,
  taxes: zod.array(Tax).optional(),
  amount: Amount,
  taxAmount: Amount,
  measurement_unit_symbol: zod.string().optional(),
  description: zod.string().optional(),
});
// eslint-disable-next-line no-redeclare
export type Product = zod.infer<typeof Product>;

// Define the extra_details schema
export const ExtraDetails = zod.object({
  bank_details: zod.string().optional(),
  terms_and_conditions: zod.string().optional(),
  shipping_address: zod.string().optional(),
  reference_no: zod.string().optional(),
  dispatch_details: zod.string().optional(),
  invoice_number_prefix: zod.string().optional(),
});
export type ExtraDetails = zod.infer<typeof ExtraDetails>;

export const Transaction = zod.object({
  id: zod.string().optional(),
  type: zod.nativeEnum(TransactionType),
  date: zod.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: 'Invalid date',
  }),
  credit_days: zod.number().optional(),
  due_date: zod
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
      message: 'Invalid date',
    })
    .optional(),
  description: zod.string().optional(),
  amount: Amount,
  party: Party.optional().nullable(),
  products: zod.array(Product),
  paymentType: zod.nativeEnum(PaymentType),
  banks: zod.array(Bank),
  bankTotal: Amount,
  invoice_number: zod.string().optional(),
  auto_invoice_number: zod.union([zod.string().optional(), zod.number().optional()]),
  tags: zod.array(zod.union([Tag, zod.number()])).optional(),
  status: zod.nativeEnum(TransactionStatus),
  extra_details: ExtraDetails.optional(),
});
// eslint-disable-next-line no-redeclare
export type Transaction = zod.infer<typeof Transaction>;

export const TransactionSuperRefine = (value: Transaction, ctx: zod.RefinementCtx) => {
  if (value.type !== TransactionType.Journal && value.type !== TransactionType.StockJournal && !value.party) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: 'party is required',
    });
  }

  if ([TransactionType.Sale, TransactionType.Purchase].includes(value.type)) {
    if (!value.products || value.products.length === 0) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'product is required',
      });
    }

    if (value.products) {
      value.products.forEach(product => {
        if (!product.quantity) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: `quantity is required for product`,
          });
        }

        if (!product.rate) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: `rate is required for product`,
          });
        }
      });
    }
  }

  if ([TransactionType.Sale, TransactionType.Purchase].includes(value.type)) {
    if (!value.products || value.products.length === 0) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'account is required',
      });
    }

    if (value.products) {
      value.products.forEach(product => {
        if (!product.quantity) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: `quantity is required for product`,
          });
        }
      });
    }
  }
};

export enum RequestStatus {
  PENDING = 'pending',
  READY = 'ready',
  ERRORED = 'errored',
  NONE = 'none',
}
export type Validator = ZodSchema | ((element: number | string) => string);

export type GSTConfigs = {
  igst: Array<string>;
  cgst: Array<string>;
  sgst: Array<string>;
};
export type MiscConfigs = {
  invoice_number_prefix: string;
};

export enum FormType {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export type Entity = {
  name: string;
  description: string;
  gst: string;
};

export enum ViewType {
  Summary = 'summary',
  Detailed = 'detailed',
}

export const Account = zod.object({
  id: zod.string().optional(),
  name: zod.string().min(1, { message: 'Name should be atleast 1 characters long' }),
  master_category_id: zod
    .string({ required_error: 'Category is required' })
    .uuid({ message: 'Invalid category, please select a category' }),
  master_category_name: zod.string().optional(),
  pan: PAN,
  gst: GST,
  address: zod.string().optional(),
  postal_code: zod
    .union([
      zod.string().min(5, { message: 'Postal code should be atleast 5 characters long' }).optional(),
      zod.literal('').optional(),
    ])
    .nullish(),
  balance_amount: NewAmount,
  hsn_code: zod.string().optional(),
  emails: zod.array(Email).optional(),
  // tags: zod.array(Tag).optional(),
  taxes: zod.array(Tax).optional(),
  can_delete: zod.boolean().optional(),
});

//  eslint-disable-next-line no-redeclare
export type Account = zod.input<typeof Account>;

export const TaxSuperRefine = (value: { taxes?: Tax[] }, ctx: zod.RefinementCtx) => {
  const taxeAccountIds = value.taxes?.map(t => t.account_id);
  if (taxeAccountIds && new Set(taxeAccountIds).size !== taxeAccountIds.length) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: `Tax entry with duplicate accounts, there can be only one entry per account`,
    });
  }

  value.taxes?.forEach((tax, idx) => {
    if (!tax.rate) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: `rate is required for tax`,
        path: ['taxes', idx, 'rate'],
      });
    }

    if (!tax.account_id) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: `account is required for tax`,
        path: ['taxes', idx, 'account_id'],
      });
    }

    if (!tax.transaction_types?.length) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: `transaction type is required for tax`,
        path: ['taxes', idx, 'transaction_types'],
      });
    }
  });
};

export type SelectedTransactionIdsSet = { [key: string]: boolean };

export type TransactionResponse = {
  opening_balance?: number | null;
  closing_balance?: number | null;
  transactions: TransactionEntry[];
};

export type TransactionEntry = Transaction & {
  entries: Array<Entry>;
  grouped_amount?: number;
};

export enum ProductAssetType {
  Current = 'current',
  Fixed = 'fixed',
}

export const IntToProductAssetType = {
  0: ProductAssetType.Current,
  1: ProductAssetType.Fixed,
};

export const IntToFieldType = {
  0: 'party',
  1: 'product',
  2: 'tax',
  3: 'bank',
  4: 'unknown',
};

export const THROTTLE_TIME = 800;

export type AccountSearchParams = {
  account_category_id: string;
};
